import { type FunctionComponent, useCallback, useMemo } from "react";
import { graphql, useLazyLoadQuery } from "react-relay";
import { Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";

import { BaseLinkContextProvider } from "shared/components/common/base-link/context";
import { tracking } from "shared/data";
import { useResponsive, useTrackImpressionEventByIntersection } from "shared/hooks";
import { Edition, useEditionValue } from "shared/lib/edition";
import { theme } from "shared/lib/styles";
import { notEmpty } from "shared/lib/utils";

import { sendGA4Tracking } from "scmp-app/components/tracking/google-analytics-4/apis";
import type { RecirculationWidgetImpressionEvent } from "scmp-app/components/tracking/google-analytics-4/types";
import type { aroundHomeV2Query } from "scmp-app/queries/__generated__/aroundHomeV2Query.graphql";

import { pickRandomItems } from "./helper";
import { HomeV2AroundItem } from "./item";
import {
  Container,
  GridContainer,
  GridItem,
  StyledArrowNext,
  StyledArrowPrevious,
  StyledSwiper,
  StyledSwiperSlide,
  SwiperContainer,
  SwiperWrapper,
  Title,
} from "./styles";

type Props = {
  className?: string;
  existingEntityIds: string[];
  randomSeed: string;
};

export const HomeV2Around: FunctionComponent<Props> = ({
  className,
  existingEntityIds,
  randomSeed,
}) => {
  const edition = useEditionValue();

  // As this query is too large, better separate it out to prevent one slow query
  const queryReference = useLazyLoadQuery<aroundHomeV2Query>(
    graphql`
      query aroundHomeV2Query($excludeEntityIds: [String], $morningStudioQueueName: String!) {
        # Hong Kong
        hongKong: queue(filter: { name: "section_top_2" }) {
          items(first: 6, exclude: { entityIds: $excludeEntityIds }) {
            edges {
              node {
                ... on Content {
                  entityId
                }
              }
              ...itemHomeV2AroundSCMPQueueItemsEdge
            }
          }
        }
        # Asia
        asia: queue(filter: { name: "section_top_3" }) {
          items(first: 6, exclude: { entityIds: $excludeEntityIds }) {
            edges {
              node {
                ... on Content {
                  entityId
                }
              }
              ...itemHomeV2AroundSCMPQueueItemsEdge
            }
          }
        }
        # World
        world: queue(filter: { name: "section_top_5" }) {
          items(first: 6, exclude: { entityIds: $excludeEntityIds }) {
            edges {
              node {
                ... on Content {
                  entityId
                }
              }
              ...itemHomeV2AroundSCMPQueueItemsEdge
            }
          }
        }
        # Business
        business: queue(filter: { name: "section_top_92" }) {
          items(first: 6, exclude: { entityIds: $excludeEntityIds }) {
            edges {
              node {
                ... on Content {
                  entityId
                }
              }
              ...itemHomeV2AroundSCMPQueueItemsEdge
            }
          }
        }
        # Economy
        economy: queue(filter: { name: "section_top_328461" }) {
          items(first: 6, exclude: { entityIds: $excludeEntityIds }) {
            edges {
              node {
                ... on Content {
                  entityId
                }
              }
              ...itemHomeV2AroundSCMPQueueItemsEdge
            }
          }
        }
        # Tech
        tech: queue(filter: { name: "section_top_36" }) {
          items(first: 6, exclude: { entityIds: $excludeEntityIds }) {
            edges {
              node {
                ... on Content {
                  entityId
                }
              }
              ...itemHomeV2AroundSCMPQueueItemsEdge
            }
          }
        }
        # Comment
        comment: queue(filter: { name: "section_top_93" }) {
          items(first: 6, exclude: { entityIds: $excludeEntityIds }) {
            edges {
              node {
                ... on Content {
                  entityId
                }
              }
              ...itemHomeV2AroundSCMPQueueItemsEdge
            }
          }
        }
        # Sport
        sport: queue(filter: { name: "section_top_95" }) {
          items(first: 6, exclude: { entityIds: $excludeEntityIds }) {
            edges {
              node {
                ... on Content {
                  entityId
                }
              }
              ...itemHomeV2AroundSCMPQueueItemsEdge
            }
          }
        }
        # Postmag
        postmag: queue(filter: { name: "section_top_71" }) {
          items(first: 6, exclude: { entityIds: $excludeEntityIds }) {
            edges {
              node {
                ... on Content {
                  entityId
                }
              }
              ...itemHomeV2AroundSCMPQueueItemsEdge
            }
          }
        }
        # TWIA
        twia: queue(filter: { name: "section_top_323045" }) {
          items(first: 6, exclude: { entityIds: $excludeEntityIds }) {
            edges {
              node {
                ... on Content {
                  entityId
                }
              }
              ...itemHomeV2AroundSCMPQueueItemsEdge
            }
          }
        }
        # Video Top
        video: queue(filter: { name: "video_homepage_top" }) {
          items(first: 6, exclude: { entityIds: $excludeEntityIds }) {
            edges {
              node {
                ... on Content {
                  entityId
                }
              }
              ...itemHomeV2AroundSCMPQueueItemsEdge
            }
          }
        }
        # SCMP Cooking
        cooking: queue(filter: { name: "latest_recipe" }) {
          items(first: 6, exclude: { entityIds: $excludeEntityIds }) {
            edges {
              node {
                ... on Content {
                  entityId
                }
              }
              ...itemHomeV2AroundSCMPQueueItemsEdge
            }
          }
        }
        # KPOP
        kPop: queue(filter: { name: "section_top_507629" }) {
          items(first: 6, exclude: { entityIds: $excludeEntityIds }) {
            edges {
              node {
                ... on Content {
                  entityId
                }
              }
              ...itemHomeV2AroundSCMPQueueItemsEdge
            }
          }
        }
        # MorningStudio
        # items(first: 8) == ENTRY_ITEM_COUNT + CANDIDATE_ITEM_COUNT (see components/home-v2/constants.ts)
        morningStudio: queue(filter: { name: $morningStudioQueueName }) {
          items(first: 8, exclude: { entityIds: $excludeEntityIds }) {
            edges {
              node {
                ... on Content {
                  entityId
                }
              }
              ...itemHomeV2AroundSCMPQueueItemsEdge
            }
          }
        }
      }
    `,
    {
      excludeEntityIds: existingEntityIds,
      morningStudioQueueName: `homepage_morning_studio_${edition}`,
    },
    {
      fetchPolicy: "store-or-network",
    },
  );

  const widgetItems = useMemo(() => {
    const itemConfigs: Array<{
      dataKeyName: keyof typeof queryReference;
      name: string;
      skipInEditions?: Edition[];
      urlAlias: string;
      withSCMPLabLogo?: boolean;
    }> = [
      {
        dataKeyName: "hongKong",
        name: "HONG KONG",
        skipInEditions: [Edition.International, Edition.HongKong],
        urlAlias: "/news/hong-kong",
      },
      {
        dataKeyName: "asia",
        name: "ASIA",
        urlAlias: "/news/asia",
      },
      {
        dataKeyName: "world",
        name: "WORLD",
        urlAlias: "/news/world",
      },
      {
        dataKeyName: "business",
        name: "BUSINESS",
        urlAlias: "/business",
      },
      {
        dataKeyName: "economy",
        name: "ECONOMY",
        urlAlias: "/economy",
      },
      {
        dataKeyName: "tech",
        name: "TECH",
        urlAlias: "/tech",
      },
      {
        dataKeyName: "comment",
        name: "COMMENT",
        urlAlias: "/comment",
      },
      {
        dataKeyName: "sport",
        name: "SPORT",
        urlAlias: "/sport",
      },
      {
        dataKeyName: "postmag",
        name: "POSTMAG",
        urlAlias: "/postmag",
      },
      {
        dataKeyName: "twia",
        name: "THIS WEEK IN ASIA",
        skipInEditions: [Edition.Asia],
        urlAlias: "/week-asia",
      },
      {
        dataKeyName: "video",
        name: "VIDEO",
        urlAlias: "/video",
      },
      {
        dataKeyName: "cooking",
        name: "SCMP COOKING",
        urlAlias: "/cooking",
      },
      {
        dataKeyName: "kPop",
        name: "K-POP",
        urlAlias: "/k-pop",
      },
      {
        dataKeyName: "morningStudio",
        name: "MORNING STUDIO",
        urlAlias: "https://morningstudio.scmp.com/",
      },
    ];

    const processedEntityIds: string[] = [];

    return itemConfigs.map(({ dataKeyName, name, skipInEditions, urlAlias, withSCMPLabLogo }) => {
      if (skipInEditions?.includes(edition)) return;

      const resultItems = (() => {
        const edges = queryReference[dataKeyName]?.items?.edges ?? [];

        if (dataKeyName === "morningStudio") {
          const items = edges.slice(4, 8);
          return notEmpty(items) ? pickRandomItems(items, 2, randomSeed) : [];
        }

        const candidateEdges = edges.filter(
          edge => !processedEntityIds.includes(edge.node.entityId ?? ""),
        );
        return candidateEdges.slice(0, 3);
      })();

      if (!notEmpty(resultItems)) return;
      processedEntityIds.push(...resultItems.map(edge => edge.node.entityId ?? ""));
      return {
        dataKeyName,
        items: resultItems,
        name,
        urlAlias,
        withSCMPLabLogo: withSCMPLabLogo ?? false,
      };
    });
  }, [edition, queryReference, randomSeed]);

  const isTabletUp = useResponsive(theme.breakpoints.up("tablet"), true);

  const renderSwiper = () => (
    <SwiperContainer>
      <SwiperWrapper>
        <StyledSwiper
          breakpoints={{
            0: {
              spaceBetween: 32,
            },
          }}
          freeMode={true}
          modules={[Navigation]}
          navigation={{
            disabledClass: "around-swiper-button-disabled",
            nextEl: ".around-swiper-button-next",
            prevEl: ".around-swiper-button-prev",
          }}
          pagination={false}
          slidesOffsetAfter={isTabletUp ? 0 : 20}
          slidesOffsetBefore={isTabletUp ? 0 : 20}
          slidesPerGroupAuto={true}
          slidesPerView="auto"
        >
          {widgetItems.map(
            widgetItem =>
              widgetItem && (
                <StyledSwiperSlide key={widgetItem.name}>
                  <HomeV2AroundItem
                    isMorningStudio={widgetItem.dataKeyName === "morningStudio"}
                    reference={widgetItem.items}
                    title={widgetItem.name}
                    urlAlias={widgetItem.urlAlias}
                    withSCMPLabLogo={widgetItem.withSCMPLabLogo}
                  />
                </StyledSwiperSlide>
              ),
          )}
        </StyledSwiper>
        <div className="swiper-button around-swiper-button-prev">
          <StyledArrowPrevious />
        </div>
        <div className="swiper-button around-swiper-button-next">
          <StyledArrowNext />
        </div>
      </SwiperWrapper>
    </SwiperContainer>
  );

  const renderGrid = () => (
    <GridContainer>
      {widgetItems.map(
        widgetItem =>
          widgetItem && (
            <GridItem key={widgetItem.name}>
              <HomeV2AroundItem
                isMorningStudio={widgetItem.dataKeyName === "morningStudio"}
                reference={widgetItem.items}
                title={widgetItem.name}
                urlAlias={widgetItem.urlAlias}
                withSCMPLabLogo={widgetItem.withSCMPLabLogo}
              />
            </GridItem>
          ),
      )}
    </GridContainer>
  );

  const impressionGA4Event = useCallback<() => RecirculationWidgetImpressionEvent>(
    () => ({
      action: "imp",
      category: "recirculation",
      customized_parameters: {
        list_of_articles: widgetItems
          .flatMap(widgetItem => widgetItem?.items.map(({ node }) => node.entityId))
          .filter(notEmpty)
          .join(","),
        page_type: "homepage",
        widget_name: "around_scmp",
      },
      subcategory: "widget",
    }),
    [widgetItems],
  );

  const { captureTrackImpressionEventTargetElement } =
    useTrackImpressionEventByIntersection<RecirculationWidgetImpressionEvent>({
      ga4TrackingHandler: sendGA4Tracking,
      getGa4Event: impressionGA4Event,
      options: { isSendGA4Tracking: true, shouldSendOnce: true },
    });

  return (
    <BaseLinkContextProvider
      customQueryParameters={{
        module: tracking.module.AroundSCMP,
      }}
    >
      <Container className={className} ref={captureTrackImpressionEventTargetElement}>
        <Title>Around SCMP</Title>
        {renderGrid()}
        {renderSwiper()}
      </Container>
    </BaseLinkContextProvider>
  );
};

HomeV2Around.displayName = "HomeV2Around";
